<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import FileDownloadLink from "@/components/shared/FileDownloadLink.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import { capitalize, slugify } from "@/helpers/filters";
import { get, post, put } from "@/requests/server";
import dayjs from "dayjs";

export default {
  name: "YearlyIncomeReportsBox",
  components: {
    FileDownloadLink,
    PaginatedTable,
    FormsValidatedInput,
    IconButton,
  },
  data() {
    return {
      loading: false,
      year: null,
      user: null,
      yearlyReport: null,
      pollTimeout: null,
      userReportsColumns: [
        new Column("id", this.$t("lists.id"), "id", { showByDefault: false }),
        Column.withRelation(
          "user.full_name",
          capitalize(this.$t("users.fields.user")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            for: "admin",
          },
          "user_id",
          {
            urlFct: (item) => (item.user.deleted_at ? null : `/admin/users/${item.user.id}`),
          }
        ),
        Column.withoutFilter(
          "path",
          capitalize(this.$t("views.admin.yearly_income_report_box.fields.path")),
          "text",
          { sortable: false }
        ),
      ],
    };
  },
  computed: {
    yearOptions() {
      let firstYear = 2020;
      let currentYear = dayjs().year();
      let options = [];
      // We do not add current year, since the report would not be complete
      for (let year = firstYear; year < currentYear; year++) {
        options.push({ value: year, text: year });
      }
      return options;
    },
    reportStatusText() {
      if (this.yearlyReport.status === "failed") {
        return this.$t("views.admin.yearly_income_report_box.status_failed");
      }

      if (this.yearlyReport.status === "generating") {
        return this.$t("views.admin.yearly_income_report_box.status_generating");
      }

      if (this.yearlyReport.status === "generated") {
        return this.$t("views.admin.yearly_income_report_box.status_generated");
      }

      return this.$t("views.admin.yearly_income_report_box.status_published");
    },
    reportStatusVariant() {
      if (!this.yearlyReport) {
        return null;
      }
      if (this.yearlyReport.status === "failed") {
        return "danger";
      }

      if (this.yearlyReport.status === "generating") {
        return "warning";
      }

      if (this.yearlyReport.status === "generated") {
        return "primary";
      }

      return "success";
    },
  },
  beforeDestroy() {
    this.stopPoll();
  },
  methods: {
    stopPoll() {
      clearTimeout(this.pollTimeout);
      this.pollTimeout = null;
    },
    poll(pollCount = 0) {
      if (pollCount > 30) {
        this.stopPoll();
        return;
      }

      if (this.pollTimeout) {
        clearTimeout(this.pollTimeout);
      }
      this.pollTimeout = setTimeout(async () => {
        await this.getYearlyReport();
        if (this.yearlyReport.status === "generating") {
          this.poll(pollCount + 1);
        } else {
          this.stopPoll();
        }
      }, 1000); // once per second
    },
    async generateAllYearlyReports() {
      const { data } = await post(
        `/reports/yearly/${this.year}/income/generate`,
        {},
        {
          notifications: {
            action: this.$t("views.admin.yearly_income_report_box.notifications.generate.action"),
          },
        }
      );
      this.yearlyReport = data;
      this.poll();
    },
    async selectYear() {
      this.stopPoll();
      await this.getYearlyReport();
      if (this.yearlyReport?.status === "generating") {
        this.poll();
      }
    },
    async getYearlyReport() {
      this.loading = true;
      try {
        const { data } = await get(`/reports/yearly/${this.year}/income`, {
          requestOptions: {
            expects: [404],
          },
          cleanupCallback: () => (this.loading = false),
          notifications: {
            action: this.$t("views.admin.yearly_income_report_box.notifications.search.action"),
          },
        });

        this.yearlyReport = data;
      } catch (e) {
        if (e.response.status === 404) {
          this.yearlyReport = null;
          return;
        }
        throw e;
      }
    },
    async publish() {
      if (!this.yearlyReport || this.yearlyReport.status !== "generated" || this.loading) {
        return;
      }

      const confirm = await this.$bvModal.msgBoxConfirm(
        this.$t("views.admin.yearly_income_report_box.publish_confirm_message"),
        {
          title: this.$t("views.admin.yearly_income_report_box.publish_confirm_title"),
          okTitle: capitalize(this.$t("views.admin.yearly_income_report_box.publish_confirm_ok")),
          cancelTitle: capitalize(this.$t("forms.cancel")),
          okVariant: "success",
          cancelVariant: "ghost-secondary",
          bodyClass: "whitespace-pre",
        }
      );

      if (!confirm) {
        return;
      }

      const { data } = await put(
        `/reports/${this.yearlyReport.id}/publish`,
        {},
        {
          notifications: {
            action: this.$t("views.admin.yearly_income_report_box.notifications.publish.action"),
          },
        }
      );
      this.yearlyReport = data;
    },
    formatReportFilename(year, userFullName) {
      return this.$t("views.admin.yearly_income_report_box.report_file_name", {
        year: year,
        user_name: slugify(userFullName),
      });
    },
  },
};
</script>

<template>
  <b-card>
    <h3>{{ $t("views.admin.yearly_income_report_box.title") }}</h3>

    <b-form-group :label="$t('views.admin.yearly_income_report_box.year')">
      <b-select v-model="year" :options="yearOptions" @change="selectYear"> </b-select>
    </b-form-group>

    <b-tabs v-if="year" content-class="pt-3">
      <b-tab
        :title="$t('views.admin.yearly_income_report_box.generate_for_all')"
        :class="{ loading: loading && !pollTimeout }"
      >
        <div v-if="yearlyReport">
          <div class="properties">
            <div>
              <div class="label">
                {{ $t("views.admin.yearly_income_report_box.creation_date") }}
              </div>
              <div>{{ yearlyReport.created_at | datetime }}</div>
            </div>
            <div>
              <div class="label">{{ $t("views.admin.yearly_income_report_box.status") }}</div>
              <div>
                <b-badge :variant="reportStatusVariant">{{ reportStatusText }}</b-badge>
              </div>
            </div>
            <div v-if="yearlyReport.status === 'generating'">
              <div class="label">{{ $t("views.admin.yearly_income_report_box.progress") }}</div>
              <div class="progress-wrapper">
                <b-progress :value="yearlyReport.progress" animated height="8px" class="progress" />
                <icon-button
                  role="load"
                  :onclick="getYearlyReport"
                  size="inline"
                  :loading="loading || !!pollTimeout"
                />
              </div>
            </div>
          </div>

          <div v-if="yearlyReport.status === 'generated'" class="button-list mt-3">
            <icon-button
              variant="outline-warning"
              icon="arrow-counterclockwise"
              type="button"
              :onclick="generateAllYearlyReports"
              :loading="!!pollTimeout"
              >{{ $t("views.admin.yearly_income_report_box.regenerate") }}
            </icon-button>

            <icon-button role="send" type="button" :onclick="publish">{{
              $t("views.admin.yearly_income_report_box.publish")
            }}</icon-button>
          </div>
        </div>
        <div v-else-if="year && !loading">
          <icon-button role="send" type="button" :onclick="generateAllYearlyReports"
            >{{ $t("views.admin.yearly_income_report_box.generate_for_all_button") }}
          </icon-button>
        </div>
        <hr v-if="yearlyReport && yearlyReport.status !== 'generating'" />

        <div v-if="yearlyReport && yearlyReport.status !== 'generating'" class="table-wrapper">
          <paginated-table
            ref="table"
            :fetch-params="{
              publishable_report_id: yearlyReport.id,
            }"
            endpoint="user_reports"
            :columns="userReportsColumns"
            class="mt-3"
            :show-action-column="false"
            :show-generate-csv="false"
          >
            <template #head>
              <h3>
                {{ $t("views.admin.yearly_income_report_box.reports") }}
              </h3>
            </template>
            <template #cell(path)="{ item }">
              <file-download-link
                v-if="item.path"
                :api-url="`user_reports/${item.id}/download`"
                :filename="formatReportFilename(yearlyReport.report_key, item.user.full_name)"
                download-as-icon
              />
              <div v-else>
                {{ $t("views.admin.yearly_income_report_box.not_generated") }}
              </div>
            </template>
          </paginated-table>
        </div>
      </b-tab>
      <b-tab :title="$t('views.admin.yearly_income_report_box.generate_for_user')">
        <forms-validated-input
          :value="user ? user.id : null"
          type="relation"
          name="yearly-report-user"
          :label="$t('views.admin.yearly_income_report_box.user')"
          :query="{
            slug: 'users',
            value: 'id',
            text: 'full_name',
            details: 'id',
            details_label: 'user_id',
            params: {
              for: 'admin',
            },
          }"
          @relation="user = $event"
        ></forms-validated-input>
        <div class="mt-3 button-list align-items-center">
          <file-download-link
            v-if="user && year"
            :api-url="`/reports/yearly/${year}/income/${user.id}/generate`"
            :filename="formatReportFilename(year, user.full_name)"
            :button-label="$t('views.admin.yearly_income_report_box.generate_for_user_button')"
            download-as-icon
          />
        </div>
      </b-tab>
    </b-tabs>
  </b-card>
</template>

<style scoped lang="scss">
.loading {
  opacity: 0.5;
}
.properties {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 2rem;
}
.progress-wrapper {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}
.progress {
  width: 5rem;
}
.label {
  font-weight: bold;
}
.tab-content {
  padding-top: 1rem;
}
</style>
